import { fetchBaseQuery, buildCreateApi, coreModule, reactHooksModule } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/dist/query/react/index';

import { normalizeUrl } from '#helper/normalize-url';
import { mutex } from '#adapter/mutex';

import { environment } from '#environment';

import type { base as baseApiType } from '.';

/*
    Если у вас есть необходимость в использование контекста хранилища для createApi.
    Вы это можете сделать здесь по нижеприведенному примеру.

    https://redux-toolkit.js.org/rtk-query/usage/customizing-create-api

    Example:

    import { createDispatchHook, createSelectorHook, createStoreHook } from 'react-redux';

    export const createApi = buildCreateApi(
        coreModule(),
        reactHooksModule({
            useDispatch: createDispatchHook(context),
            useSelector: createSelectorHook(context),
            useStore   : createStoreHook(context)
        })
    );
*/
export const createApi = buildCreateApi(coreModule(), reactHooksModule());

/*
    Подробное описание допустимых параметров и примеров `fetchBaseQuery` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/fetchBaseQuery#parameters
*/
export const baseQuery = (baseUrl: string) => {
    const queryUpgradeAuth = fetchBaseQuery({
        baseUrl       : normalizeUrl(environment.hostCallCenter),
        headers       : { 'cache-control': 'no-cache' },
        credentials   : 'include',
        prepareHeaders: (headers) => {
            const accessToken = localStorage.getItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

            accessToken && headers.append('Authorizev3', accessToken);

            return headers;
        }
    });

    const query = fetchBaseQuery({
        baseUrl       : normalizeUrl(environment.hostCallCenter + baseUrl),
        headers       : { 'cache-control': 'no-cache' },
        prepareHeaders: (headers) => {
            const token = localStorage.getItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

            if(token) {
                headers.append('AuthorizeV3', token);
            }

            return headers;
        },
        credentials: 'include'
    });

    return async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: Record<PropertyKey, unknown>) => {
        await mutex.waitForUnlock();

        let result = await query(args, api, extraOptions);

        if(result.error?.status === 401 || result.error?.status === 403) {
            if(!mutex.isLocked()) {
                const release = await mutex.acquire();

                try {
                    const accessToken = localStorage.getItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

                    if(accessToken) {
                        await queryUpgradeAuth({
                            method: 'POST',
                            url   : '/upgrade-cookie-authv3'
                        }, api, extraOptions);
                    }

                    result = await query(args, api, extraOptions);
                } finally {
                    release();
                }
            } else {
                await mutex.waitForUnlock();

                result = await query(args, api, extraOptions);
            }
        }

        return result;
    };
};

/*
    Подробное описание взаимодействия с `enhanceEndpoints` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#enhanceendpoints
*/
export const enhanceEndpoints = (api: typeof baseApiType) => {
    api.enhanceEndpoints({
        addTagTypes: ['dialog', 'dialogs'],
        endpoints  : {
            v1SuppliersAppealsAttachmentsBucketIdKeyIdGet: {
                query: (params) => ({
                    method         : 'GET',
                    url            : `/v1/suppliers/appeals/attachments/${params.path.bucket_id}/${params.path.key_id}`,
                    responseHandler: async (response) => {
                        return response;
                    }
                })
            },
            v1SuppliersDialogsGet: {
                providesTags: ['dialogs']
            },
            v1SuppliersDialogsDialogIdGet: {
                providesTags: (response, error, params) => {
                    if(params.path.appeal_id) {
                        return [{
                            type: 'dialog',
                            id  : Number(params.path.appeal_id)
                        }];
                    }

                    return ['dialog'];
                }
            },
            v1SuppliersUsersAppealsPost: {
                invalidatesTags: (response, error, params) => {
                    // Наличие `parentComId` означает что мы добавляем в существующий диалог сообщение.
                    // Инвалидируется диалог в который добавили сообщение.
                    if(typeof params.body?.parentComId === 'number') {
                        return [{
                            type: 'dialog',
                            id  : params.body.parentComId
                        }];
                    }

                    // Отсутствие `parentComId` означает что мы создаем новый диалог.
                    // Инвалидируется список диалогов.
                    return ['dialogs'];
                }
            }
        }
    });
};

/*
    Подробное описание взаимодействия с `injectEndpoints` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#injectendpoints
*/
export const injectEndpoints = (api: typeof baseApiType) => {
    return api.injectEndpoints({
        endpoints: () => ({})
    });
};
